import { useMemo, useState } from "react";
import type { ClubOption } from "../game/types";
import { Crest, Money, Stars, StrengthBadge } from "../ui/atoms";
import { rotuloDeReputacao, useT } from "../i18n";

/**
 * Escolha de clube.
 *
 * Mesma decisão que abre Elifoot e Brasfoot: escolher onde começar a carreira.
 * A lista mostra o que importa para essa decisão, que é o tamanho do elenco, a
 * reputação e o caixa.
 */
/** Teto de treinadores no mesmo aparelho. O motor aceita mais, a mesa não. */
const MAX_ASSENTOS = 8;

export function NewGame({
  options,
  generationMs,
  onStart,
  aoCriarClube,
  onBack,
  permiteHotseat,
}: {
  options: ClubOption[];
  generationMs: number;
  onStart: (seats: Array<{ clubId: string; managerName: string }>) => void;
  /**
   * Abre o criador de clube, levando o nome do treinador e o país escolhido.
   *
   * O país vem daqui porque é aqui que ele é escolhido, e o clube criado nasce
   * na última divisão dele.
   */
  aoCriarClube: (countryId: string, managerName: string) => void;
  onBack: () => void;
  /**
   * Vários treinadores no mesmo aparelho só existem no modo 1 Jogador.
   *
   * Na carreira da conta seria contradição: a carreira é de uma conta, e os
   * outros sete assentos não teriam conta nenhuma.
   */
  permiteHotseat: boolean;
}) {
  const t = useT();
  const [managerName, setManagerName] = useState("Gabriel");
  const [country, setCountry] = useState<string>(options[0]?.countryName ?? "");
  const [selected, setSelected] = useState<string | null>(null);
  /**
   * Assentos já confirmados, do segundo treinador em diante.
   *
   * O primeiro é sempre quem está montando a partida, e continua escolhendo do
   * jeito de sempre. Quem quiser jogar sozinho nem percebe que isto existe.
   */
  const [assentos, setAssentos] = useState<Array<{ clubId: string; managerName: string }>>([]);

  const countries = useMemo(
    () => [...new Set(options.map((option) => option.countryName))].sort(),
    [options],
  );
  /**
   * Toda carreira começa na última divisão do país.
   *
   * Não é um padrão que dá para trocar, é a regra: o jogo é sobre construir um
   * legado, e começar podendo escolher o gigante da primeira divisão é começar
   * pelo fim. Quem quiser treinar o time grande chega nele ganhando.
   *
   * "Última" e não "quarta" fixa: o número de divisões vem do banco, e um
   * datapack de terceiro pode trazer um país com três ou com seis. O maior
   * número de divisão é sempre a mais baixa.
   */
  const tier = useMemo(() => {
    const doPais = options.filter((option) => option.countryName === country);
    return doPais.reduce((mais, option) => Math.max(mais, option.tier), 1);
  }, [options, country]);

  const visible = useMemo(
    () => options.filter((option) => option.countryName === country && option.tier === tier),
    [options, country, tier],
  );

  const chosen = options.find((option) => option.id === selected) ?? null;
  /*
   * O código do país, tirado do primeiro clube dele.
   *
   * A tela escolhe país pelo nome, que é o que se lê, mas o motor indexa por
   * código. Como todo clube carrega os dois, o primeiro da lista responde.
   */
  const paisEscolhido = options.find((option) => option.countryName === country)?.countryId ?? "";
  const tomados = new Set(assentos.map((assento) => assento.clubId));
  const podeAdicionar =
    permiteHotseat &&
    chosen !== null &&
    !tomados.has(chosen.id) &&
    managerName.trim().length > 0 &&
    assentos.length + 1 < MAX_ASSENTOS;

  function confirmar(): void {
    if (!chosen || managerName.trim().length === 0) return;
    onStart([{ clubId: chosen.id, managerName: managerName.trim() }, ...assentos]);
  }

  function adicionarAssento(): void {
    if (!podeAdicionar || !chosen) return;
    setAssentos((atual) => [...atual, { clubId: chosen.id, managerName: managerName.trim() }]);
    setSelected(null);
    setManagerName("");
  }

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "28px var(--gutter) 40px" }}>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 18, gap: 12 }}>
        <div>
          <span className="label">{t("novoJogo.rotulo")}</span>
          <h1 style={{ fontSize: 26, marginTop: 4 }}>{t("novoJogo.titulo")}</h1>
          <p className="muted" style={{ margin: "5px 0 0" }}>
            {t("novoJogo.resumoDaGeracao", { clubes: options.length, ms: generationMs })}
          </p>
        </div>
        <button onClick={onBack}>{t("comum.voltar")}</button>
      </div>

      <div className="card" style={{ padding: 14, marginBottom: 14 }}>
        <div className="row" style={{ flexWrap: "wrap", gap: 14 }}>
          <label className="row" style={{ gap: 8 }}>
            <span className="label-muted">{t("novoJogo.seuNome")}</span>
            <input
              value={managerName}
              onChange={(event) => setManagerName(event.target.value)}
              maxLength={24}
              style={{ width: 170 }}
            />
          </label>
          <label className="row" style={{ gap: 8 }}>
            <span className="label-muted">{t("novoJogo.pais")}</span>
            <select
              value={country}
              onChange={(event) => {
                setCountry(event.target.value);
                setSelected(null);
              }}
            >
              {countries.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          {/* Divisão vira dado, e não escolha: é sempre a última do país. */}
          <span className="row" style={{ gap: 8 }}>
            <span className="label-muted">{t("novoJogo.divisao")}</span>
            <span style={{ fontWeight: 600 }}>
              {t("novoJogo.divisaoOpcao", { numero: tier })}
            </span>
          </span>
        </div>
      </div>

      <div className="card scroll" style={{ maxHeight: "50vh", overflow: "auto" }}>
        <table>
          <thead>
            <tr>
              <th>{t("novoJogo.colunas.clube")}</th>
              <th>{t("novoJogo.colunas.reputacao")}</th>
              <th className="right">{t("novoJogo.colunas.elenco")}</th>
              <th className="right">{t("novoJogo.colunas.caixa")}</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((option) => (
              <tr
                key={option.id}
                className="player"
                onClick={() => setSelected(option.id)}
                style={{
                  cursor: "pointer",
                  background: option.id === selected ? "var(--primary-glow)" : "transparent",
                }}
              >
                <td>
                  <span className="row" style={{ gap: 10 }}>
                    <Crest colors={option.colors} short={option.short} size={28} />
                    <strong>{option.name}</strong>
                  </span>
                </td>
                <td>
                  <span className="row" style={{ gap: 8 }}>
                    <Stars level={option.reputation} size={11} />
                    <span className="muted" style={{ fontSize: 11 }}>
                      {rotuloDeReputacao(option.reputation)}
                    </span>
                  </span>
                </td>
                <td className="right">
                  <StrengthBadge value={option.squadStrength} />
                </td>
                <td className="right">
                  <Money value={option.cash} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {permiteHotseat && assentos.length > 0 && (
        <div className="card" style={{ padding: 12, marginTop: 14 }}>
          <span className="label">{t("novoJogo.tambemVaoJogar")}</span>
          <div className="row" style={{ gap: 8, flexWrap: "wrap", marginTop: 8 }}>
            {assentos.map((assento, indice) => {
              const clube = options.find((option) => option.id === assento.clubId);
              return (
                <span
                  key={assento.clubId}
                  className="row"
                  style={{
                    gap: 8,
                    padding: "4px 10px",
                    borderRadius: 999,
                    background: "var(--surface-2)",
                  }}
                >
                  <Crest colors={clube?.colors ?? ["#2c3f33", "#2c3f33"]} short={clube?.short ?? ""} size={20} />
                  <span style={{ fontSize: 12 }}>
                    {t("novoJogo.assento", {
                      treinador: assento.managerName,
                      clube: clube?.name ?? assento.clubId,
                    })}
                  </span>
                  <button
                    className="ghost"
                    onClick={() => setAssentos((atual) => atual.filter((_, i) => i !== indice))}
                    style={{ minHeight: 20, padding: "0 6px", fontSize: 11 }}
                  >
                    {t("novoJogo.tirar")}
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}

      <div className="row" style={{ justifyContent: "space-between", marginTop: 18, gap: 12, flexWrap: "wrap" }}>
        <span className="muted">
          {chosen
            ? t("novoJogo.clubeEscolhido", { clube: chosen.name, liga: chosen.leagueName })
            : t("novoJogo.selecione")}
        </span>
        <div className="row" style={{ gap: 8 }}>
          {/*
            Fora do modo 1 Jogador este botão não existe, e não é só questão
            de estar desligado.

            Desabilitado ele continuava se oferecendo: quem entrasse pela conta
            via um botão cinza de somar treinador e uma frase falando em oito
            pessoas no mesmo aparelho, que é o contrário do que a carreira da
            conta é. A carreira é de uma conta, e os outros sete assentos não
            teriam conta nenhuma.
          */}
          {/*
            Criar clube fica ao lado de assumir, e não numa tela antes desta.

            Quem abre a nova carreira não sabe ainda se quer um clube da lista
            ou o próprio: pôr a pergunta antes obrigaria a decidir sem ver o que
            existe. Aqui a lista está na tela e a escolha é lado a lado.
          */}
          {/*
            Do mesmo peso do verde, porque são dois caminhos e não um caminho e
            uma sobra. Com o estilo neutro ao lado de um botão cheio, criar
            clube lia como opção de canto e passava batido.
          */}
          <button
            className="ghost"
            disabled={managerName.trim().length === 0 || paisEscolhido.length === 0}
            onClick={() => aoCriarClube(paisEscolhido, managerName.trim())}
            style={{ borderColor: "var(--primary)", color: "var(--primary)", fontWeight: 700 }}
          >
            {t("criador.titulo")}
          </button>

          {permiteHotseat && (
            <button
              disabled={!podeAdicionar}
              onClick={adicionarAssento}
              title={t("novoJogo.adicionarTreinadorDica")}
            >
              {t("novoJogo.adicionarTreinador")}
            </button>
          )}
          <button
            className="primary"
            disabled={!chosen || managerName.trim().length === 0}
            onClick={confirmar}
          >
            {assentos.length > 0
              ? t("novoJogo.comecarComTreinadores", { total: assentos.length + 1 })
              : t("novoJogo.assumirOComando")}
          </button>
        </div>
      </div>
    </div>
  );
}
